import React from 'react';
import { useLocation } from 'react-router';
import SVGInline from 'components/SVGInline/SVGInline';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { Breakpoints, MQ } from 'style/styled-components/cssMediaQueries';
import icons from 'style';
import { useScreenState } from 'hooks/useScreenState/useScreenState';
import translate from 'utils/fnTranslate';
import { usePageScroller } from 'components/PageScroller/usePageScroller';
import { useSearch } from 'providers/useSearch/SearchContext';
import { hexToRgba } from 'utils/fnColors';
import { useAuth } from 'providers/useAuth/AuthContext';
import { FAVORITES_CUSTOM_ATTR_ID } from 'types/RouteTypes';
import { useNavigationData } from '../../providers/useNavigation/NavigationContext';
import { getMenuItemPath, isMenuItemActive } from '../../utils/fnRouting';
import { savePageInfo } from '../../utils/pageInfoStorage';
import { MenuLink } from './Header';

export interface MenuProps {
    pageId?: string;
    scroller?: any;
    id?: string;
    className?: string;
    backgroundColor?: string;
    isIOS?: boolean;
}

interface LinkProps {
    title: string;
    path: string;
    icon?: string;
    active: boolean;
    onClick: () => void;
}

const MainMenuLink: React.FC<LinkProps> = ({ title, path, icon, active, onClick }) => (
    <MenuLink
        to={path}
        active={active}
        aria-current={active ? 'page' : undefined}
        onClick={onClick}
        data-event-name={'click'}
        data-test-id={`header-menu`}
        data-track-id={`menu_${title.toLowerCase()}`}
    >
        <SVGInline svg={icons[icon] || icon || ''} />
        <span className={'title'}>{title}</span>
    </MenuLink>
);

const BareMainMenu: React.FC<MenuProps> = ({ ...rest }) => {
    const { getNavigationItems } = useNavigationData();
    const { pathname } = useLocation();
    const { emptyStates } = useScreenState();
    const { scrollTo } = usePageScroller();
    const { exitSearch } = useSearch();
    const { isGuest } = useAuth();

    const navigationItems = getNavigationItems()?.filter((item) => !(isGuest && item.customAttributesId === FAVORITES_CUSTOM_ATTR_ID));

    return (
        <nav aria-label={translate('ARIA_MENU_MAIN')} {...rest}>
            <ul>
                {navigationItems?.map((menu, index) => {
                    const path = index === 0 ? '/' : getMenuItemPath(menu);
                    const text = translate(menu.title);
                    return (
                        <li key={index}>
                            <MainMenuLink
                                title={text}
                                path={path}
                                icon={menu.icon}
                                active={isMenuItemActive(pathname, path, index)}
                                onClick={() => {
                                    savePageInfo('');
                                    emptyStates(path);
                                    scrollTo(0);
                                    exitSearch(false);
                                }}
                            />
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const MainMenu = styled(({ isIOS, ...rest }) => <BareMainMenu {...rest} />)<{
    isIOS: boolean;
}>`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${(props) => props.theme.brandedConfig.background};
    display: block;
    border-top: 1px solid ${(props) => hexToRgba(props.theme.brandedConfig.white_color, 0.1)};

    ${MQ(Breakpoints.m)} {
        float: left;
        padding-left: 40px;
        background-color: transparent;
        position: relative;
        display: none;
        border: none;

        &:not(.hide-on-desktop) {
            display: block;
        }
    }

    & ul {
        display: flex;
        padding: 6px 0 ${(props) => (props.isIOS ? '20px' : '6px')} 0;
        justify-content: space-evenly;

        ${MQ(Breakpoints.s)} {
            padding: 14px 0 ${(props) => (props.isIOS ? '20px' : '14px')} 0;
        }

        ${MQ(Breakpoints.m)} {
            display: block;
            list-style: none;
            border: 0;
            text-align: left;
            padding: 0;
        }
    }

    & ul > li {
        font-size: 0;
        text-align: center;

        .SVGInline {
            display: block;
            height: 20px;
            vertical-align: middle;

            svg {
                height: 100%;
                width: auto;
            }

            ${MQ(Breakpoints.s)} {
                display: inline-block;
                padding-right: 12px;
            }
        }

        .title {
            display: block;
            vertical-align: middle;

            ${MQ(Breakpoints.s)} {
                display: inline-block;
            }
        }

        ${MQ(Breakpoints.m)} {
            display: inline-block;
            padding: 0 12px;

            .SVGInline {
                display: none;
            }

            .title {
                line-height: inherit;
            }
        }
    }
`;
